import React from "react";
import addDays from "date-fns/addDays";
import axios from "axios";
import moment from 'moment';
import { AlertColor, Autocomplete, Button, Link, TextField, useMediaQuery} from "@mui/material";
import { Add, DeveloperBoard,  Search } from "@mui/icons-material";
import { DataGridPro, GridCellParams, GridColumns, GridToolbarContainer } from '@mui/x-data-grid-pro';
import Title from "../../../component/Title";
import { DropDownComboOption } from "../../../component/GenericFilter";
import { globalTheme } from "../../../../common/theme";
import { useConstructor } from "../../../../common/UseConstructor";
import { GenerateStatementRecordFilter, StatementRecordFilter } from "../../../../models/classes/StatementRecordFilter";
import { CustomTablePagination } from "../../../component/CustomPagination";
import { useAppSelector } from "../../../../common/hooks";
import { RootState } from "../../../../common/Store";
import { CommonProps } from "../../../component/MainContents";
import { FormatNumber } from "../../../../common/Formatter";
import { renewToken } from "../../../../services/AuthService";
import { SnackBarMessage, SnackBarMessageProps } from "../../../component/SnackBarMessage";
import { useNavigate } from "react-router-dom";
import { OpenMode } from "../../../../common/Enums";
import { StatementRecord } from "./StatementAccessories";
import { StatementRecordSingle, StatementRecordSingleHandle } from "./StatementRecordSingle";
import { Customer } from "../../customers/CustomerAccessories";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DatePicker from "@mui/lab/DatePicker";
import AdapterDateFns from "@mui/lab/AdapterDateFns";

export function StatementRecordList(props: CommonProps) {
	const [loading, setLoading] = React.useState(true);
	const [hasError, setHasError] = React.useState(false);

    const isDebug = useAppSelector((state: RootState) => state.menuToggle.debugMode);

	const singleModalRef = React.useRef<StatementRecordSingleHandle>(null);

	const navigate = useNavigate();

    const style = { ...globalTheme() };

    const tinyScreen = useMediaQuery('(max-width:1366px)');
    const smallScreen = useMediaQuery('(max-width:1920px)');
    const normalScreen = useMediaQuery('(max-width:2560px)');

    const [fromDate, setFromDate] = React.useState<Date>(new Date());
    const [toDate, setToDate] = React.useState<Date>(new Date());
    const [selectedCustomer, setSelectedCustomer] = React.useState<DropDownComboOption>();
    const [filteredCustomers, setFilteredCustomers] = React.useState<DropDownComboOption[]>([]);

    const [searchData, setSearchData] = React.useState<any>({
        contains: true,
        searchText: "",
        customer: 0,
        from: addDays(new Date(), -60),
        to: new Date(),
        pageSize: 15,
    });

    //Dialogs and snacks
    const [snackSettings, setSnackSettings] =
      React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
      setSnackSettings({
        open: true,
        message: message,
        subMessage: subMessage,
        severity: colour,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });
    }

	function LoadData() {
		setLoading(true);
        let filter = new StatementRecordFilter({
			From: moment(fromDate).format(),
			To: moment(toDate).format(),
			CustomerID: parseInt(selectedCustomer?.value ?? "0") % 100000,
			StatementNo: ""
		});

		axios.post<StatementRecord[]>('Api/V1/Statement/StatementRecord/Search', filter, {
			headers: {
				'Content-Encoding': 'gzip'
			}
		})
			.then((result) => {
				result.data.forEach(i => {
					i.id = i.recordID;
				});

				setGridData(result.data.sort((a, b) => {
					if (a.invoiceDate > b.invoiceDate) { return -1; }
					if (a.invoiceDate < b.invoiceDate) { return 1; }
					if (a.invoiceDate === b.invoiceDate)
					{
						if (a.statementNo > b.statementNo) { return -1; }
						if (a.statementNo < b.statementNo) { return 1; }
					}
					return 0;					
				}));
				setLoading(false);
			},
				(error) => {
					setGridData([]);
					setLoading(false);
					console.log(error);
					showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
				}
			)
			.catch((error) => {
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}

	useConstructor(() => Initialise())
    function Initialise() {
        setLoading(true);
		renewToken();

		let lastMonth = new Date();
		lastMonth.setMonth((new Date()).getMonth() - 1);
		setFromDate(lastMonth);
		LoadData();
		FilterCustomersByLanguage([0]); //Always show customer names of any language
    }

	//Grid columns
	const columns: GridColumns = [
		{
			field: 'invoiceNo', headerName: 'Invoice No', flex: 2, renderCell: (params: GridCellParams) => (
				<Link
					href="#"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						EditObject(params.id.toString());
					}}
					color="inherit"
					underline="hover">
					<strong>
						{params.value}
					</strong>
				</Link>
			)
		},
		{ field: 'customerName', headerName: 'Customer', flex: 4, type: 'string' },
		{ field: 'invoiceDate', headerName: 'Date', flex: 1.1, 
			renderCell: (params: GridCellParams) => (
				<TextField variant="standard" value={moment(params.value).format('YYYY/MM/DD')} InputProps={{ disableUnderline: true }}/>
			)
		},
		{ field: 'poNo', headerName: 'PO No', flex: 4, type: 'string' },
		{ field: 'currency', headerName: 'CCY', flex: 0.5, type: 'string' },
		{ field: 'amount', headerName: 'Amount', flex: 1, type: 'number', valueFormatter: (param) => FormatNumber(param.value, 2) },
	];

	const [gridData, setGridData] = React.useState<StatementRecord[]>([]);
	const selectedData = React.useRef<StatementRecord[]>([]);

	function CustomToolbar() {
		return (
			<GridToolbarContainer style={{ display: 'flex' }}>
				<LocalizationProvider dateAdapter={AdapterDateFns}>
					<DatePicker
						key="fromDate"
						label="From"
						inputFormat="yyyy/MM/dd"
						mask="____/__/__"
						value={fromDate}
						onChange={(newValue) => setFromDate(newValue ?? new Date())}
						renderInput={(params) => 
							<TextField {...params} size="small"
								style={{ width: "160px", marginLeft: 10, marginTop: 15 }}
							/>
						}
					/>
				</LocalizationProvider>
				<div style={{ width: 10 }}></div>
				<LocalizationProvider dateAdapter={AdapterDateFns}>
					<DatePicker
						key="toDate"
						label="To"
						inputFormat="yyyy/MM/dd"
						mask="____/__/__"
						value={toDate}
						onChange={(newValue) => setToDate(newValue ?? new Date())}
						renderInput={(params) => 
							<TextField {...params} size="small"
								style={{ width: "160px", marginLeft: 10, marginTop: 15 }}
							/>
						}
					/>
				</LocalizationProvider>
				<div style={{ width: 10 }}></div>
				<Autocomplete
					limitTags={2}
					onChange={(e, selected) => CustomerChanged(selected)}

					options={filteredCustomers}
					getOptionLabel={(option: DropDownComboOption) => option.name}
					renderOption={(props, option) => {
					return (
						<li {...props} key={option.value}>
						{option.name}
						</li>
					);
					}}
					value={filteredCustomers.find(s => s.value === (selectedCustomer?.value ?? 0).toString()) || null}
					style={{ width: tinyScreen ? "274px" : (smallScreen ? "374px" : "494px"), marginLeft: 10, marginTop: 15 }}
					renderInput={(params) => (
					<TextField
						{...params}
						variant="outlined"
						label={"Customer"}
						size="small"
						fullWidth
					/>
					)}
				/>
				<div style={{ width: 10 }}></div>
				<Button variant="contained" size="medium" color="secondary" style={{ marginTop: 15, marginLeft: 5 }} startIcon={<Search />} onClick={() => LoadData()}>Search</Button>
				<div style={{ width: 20 }}></div>
				<Button variant="contained" size="medium" color="secondary" style={{ marginTop: 15, marginLeft: 5 }} startIcon={<DeveloperBoard />} onClick={() => AutoGenerateRecords()}>Create Records</Button>
				<div style={{ width: 10 }}></div>
				<Button variant="contained" size="medium" color="secondary" style={{ marginTop: 15, marginLeft: 5 }} startIcon={<Add />} onClick={() => NewObject()}>New</Button>
			</GridToolbarContainer>
		);
	}

    function CustomerChanged(newCustomer: any) {
		if (newCustomer) {
			let custId = newCustomer.value % 100000;
			let custLangId = Math.trunc(newCustomer.value / 100000);
			setSelectedCustomer(newCustomer);
			LoadCustomerInfo(custId, custLangId);
		}
		else
		{
			setSelectedCustomer(undefined);
		}
	}
  
	function LoadCustomerInfo(customerId: number, custLangId: number)
	{
		axios.get<Customer>('Api/V1/Customer/' + customerId.toString(), {
		  headers: {
			'Content-Encoding': 'gzip'
		  }
		})
		.then((result) => {
			if (result.data) 
			{
				
			}
		},
		(error) => {
			console.log(error);
			showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
		}
		)
		.catch((error) => {
		console.log(error);
		showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
		});        
	}

    function FilterCustomersByLanguage(langIds: number[]) {
		let temp: DropDownComboOption[] = props.customers;
		let result: DropDownComboOption[] = [];
		if (langIds.findIndex( x => x === 0) > -1)
		{
		  setFilteredCustomers(props.customers);
		} 
		else
		{
		  langIds.forEach((id) => {
			result = result.concat(Object.values(temp).filter(c => Math.trunc(parseInt(c.value) / 100000) === id));
		  });
		  setFilteredCustomers(result);
		}
	}
	
	function NewObject() {
		if (selectedCustomer)
		{
			let statementRecord: StatementRecord = {
				id: 0,
				recordID: 0,
				statementNo: "",
				customerID: parseInt(selectedCustomer.value) % 100000,
				custLangID: Math.trunc(parseInt(selectedCustomer.value) / 100000),
				invoiceDate: moment(new Date()).format(),
				invoiceNo: "",
				poNo: "",
				currency: "JPY",
				amount: 0,
				paid: false,
				region: "HK",
				selected: false
			}
			singleModalRef.current?.Initialise(OpenMode.NEW, statementRecord);
		}
		else
		{
			showSnackBar("Please select a customer before add!", "", "error");	
		}
	}

	function EditObject(recordId: string) {
        setLoading(true);
		axios.get<StatementRecord>('Api/V1/Statement/StatementRecord/' + recordId, {
			headers: {
			  'Content-Encoding': 'gzip'
			}
		  })
			.then((result) => {
				singleModalRef.current?.Initialise(OpenMode.EDIT, result.data);
				setLoading(false);
			},
			  (error) => {
				setLoading(false);
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			}
			)
			.catch((error) => {
			  setLoading(false);
			  console.log(error);
			  showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}
  
	function AutoGenerateRecords() {
        setLoading(true);
        let filter = new GenerateStatementRecordFilter({
			FromDate: moment(fromDate).format(),
			ToDate: moment(toDate).format()
		});

		axios.post<StatementRecord[]>('Api/V1/Statement/StatementRecord/Generate', filter, {
			headers: {
				'Content-Encoding': 'gzip'
			}
		})
			.then((result) => {
				result.data.forEach(i => {
					i.id = i.recordID;
				});

				setGridData(result.data.sort((a, b) => {
					if (a.invoiceDate > b.invoiceDate) { return -1; }
					if (a.invoiceDate < b.invoiceDate) { return 1; }
					if (a.invoiceDate === b.invoiceDate)
					{
						if (a.statementNo > b.statementNo) { return -1; }
						if (a.statementNo < b.statementNo) { return 1; }
					}
					return 0;					
				}));
				setLoading(false);
			},
				(error) => {
					setGridData([]);
					setLoading(false);
					console.log(error);
					showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
				}
			)
			.catch((error) => {
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}

    return (
        <div className={style.PageMain}>
			<Title pageTitle="Statement List" />
			<div className={style.PageContent}>
				<DataGridPro
					rows={gridData}
					columns={columns}
					pageSize={searchData.pageSize}
					onPageSizeChange={(pageSize) => setSearchData({ ...searchData, pageSize: pageSize })}
					pagination
					rowsPerPageOptions={[15, 30, 50, 100]}
					loading={loading}
					components={{
						Toolbar: CustomToolbar,
						Footer: CustomTablePagination
					}}
					componentsProps={{
						footer: { rowsPerPageOptions: [15, 30, 50, 100] }
					}}
					headerHeight={tinyScreen ? 30 : (smallScreen ? 34 : 38)}
					rowHeight={tinyScreen ? 22 : (smallScreen ? 28 : 34)}
					onRowDoubleClick={(params) => EditObject(params.row.recordID)}
				/>
			</div>

			<StatementRecordSingle
				ref={singleModalRef}
				customers={props.customers}
				regions={props.regions}
				paymentTerms={[]}
				endUsers={[]} 
				sales={[]} 
				deliveryMethods={[]} 
				currencies={props.currencies}
				onClose={(saved: boolean, statementRecord: StatementRecord) => {
					LoadData();
				} } />

			<SnackBarMessage {...snackSettings} />

        </div>
    );
}

